import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios'; // Make sure this path is correct

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5069';

interface AuthContextType {
  user: any;
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
  checkRole: (role: string) => boolean;
  loading: boolean;
  refreshUser: () => Promise<void>;
}

interface TokenPayload {
  email: string;
  exp: number;
  iat: number;
  role: string;
  [key: string]: any;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
  
    if (storedToken) {
      const decoded = jwtDecode<TokenPayload>(storedToken);
      setToken(storedToken);
      setUser(decoded); // fallback
  
      axios.get('/auth/user/profile')
        .then(res => {
          // API now returns camelCase, use directly
          setUser(res.data);
          console.log('🔄 Refreshed user profile:', res.data);
          setLoading(false);
        })
        .catch(err => {
          console.error('Failed to fetch profile:', err);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    const handleUnload = () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        navigator.sendBeacon(`${API_URL}/api/auth/logout`);
      }
    };
  
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);
  

  const login = (newToken: string) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    const decoded = jwtDecode<TokenPayload>(newToken);
    setUser(decoded);
    // 🔁 Fetch full profile after login
    refreshUser();
  };

  const logout = async () => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        await axios.post('/auth/logout', null, {
          headers: { Authorization: `Bearer ${storedToken}` },
        });
      } catch (err) {
        console.warn('Server-side logout failed:', err);
      }
    }
  
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    navigate('/login');
  };
  

  const refreshUser = async () => {
    try {
      const res = await axios.get('/auth/user/profile', {
        params: { _: Date.now() } // Cache buster
      });
      // API now returns camelCase, use directly
      setUser(res.data);
      console.log('🔄 User refreshed:', res.data);
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  const checkRole = (role: string): boolean => {
    return user?.role === role;
  };

  return (
    <AuthContext.Provider
      value={{ user, token, login, logout, checkRole, loading, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
